// src/components/Header.ts
// App top bar with CoShop branding, centered household switcher, role badge, and user menu

import type { AppState } from '../types';
import { syncManager } from '../services/sync';

export class Header {
  private el: HTMLElement;
  private syncBar: HTMLElement;

  constructor(
    private state: AppState,
    private callbacks: {
      onLogoClick: () => void;
      onHouseholdClick: () => void;
      onAuthClick: () => void;
    }
  ) {
    this.el = document.createElement('header');
    this.el.className = 'app-header';

    this.syncBar = document.createElement('div');
    this.syncBar.className = 'sync-bar';
    document.body.prepend(this.syncBar);

    syncManager.registerStatusListener((status) => {
      if (status.active || status.syncing || status.error) {
        this.syncBar.classList.add('visible');
        if (status.syncing) {
          this.syncBar.className = 'sync-bar visible syncing';
          this.syncBar.innerHTML = `<span class="sync-spinner"></span> Syncing ticks to household…`;
        } else if (status.error) {
          this.syncBar.className = 'sync-bar visible error';
          this.syncBar.innerHTML = `⚠️ ${status.error}`;
        } else {
          this.syncBar.className = 'sync-bar visible';
          this.syncBar.innerHTML = `💾 ${status.count} tick${status.count !== 1 ? 's' : ''} saved locally (offline)`;
        }
      } else {
        this.syncBar.classList.remove('visible');
      }
    });

    this.render();
  }

  public getElement(): HTMLElement {
    return this.el;
  }

  public update(state: AppState) {
    this.state = state;
    this.render();
  }

  private render() {
    const { user, authenticated, currentHousehold } = this.state;
    const role = currentHousehold?.role || 'shopper';

    const householdHtml = authenticated && currentHousehold ? `
      <button class="household-selector" id="household-btn" title="Switch or manage household">
        <span class="household-name-truncate">${escapeHtml(currentHousehold.name)}</span>
        <span class="role-badge ${role}">${role}</span>
        <span style="font-size:0.65rem;opacity:0.6;">▼</span>
      </button>
    ` : '';

    const userHtml = authenticated && user ? `
      <button class="user-avatar-btn" id="user-profile-btn" title="${escapeHtml(user.name)} (${escapeHtml(user.email)})">
        <img class="user-avatar-img" src="${user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=5C7A5F&color=fff`}" alt="${escapeHtml(user.name)}" />
      </button>
    ` : `
      <button class="btn btn-primary" id="login-nav-btn" style="padding:0.4rem 0.85rem;font-size:0.82rem;">Sign In</button>
    `;

    this.el.innerHTML = `
      <div class="header-inner">
        <div class="header-left">
          <div class="brand-logo" id="brand-logo" title="CoShop Home">
            <svg class="brand-icon" viewBox="0 0 36 36" width="30" height="30">
              <defs>
                <linearGradient id="hdr-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#4E8C56"/>
                  <stop offset="100%" stop-color="#34683C"/>
                </linearGradient>
                <linearGradient id="hdr-accent" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#F28E2B"/>
                  <stop offset="100%" stop-color="#D96E14"/>
                </linearGradient>
              </defs>
              <rect width="36" height="36" rx="9" fill="url(#hdr-grad)"/>
              <path d="M10 14 C10 12.8 11 11.5 12.5 11.5 L23.5 11.5 C25 11.5 26 12.8 26 14 L27.5 26 C27.5 27.5 26.2 28.5 24.8 28.5 L11.2 28.5 C9.8 28.5 8.5 27.5 8.5 26 Z" 
                    fill="none" stroke="#FAF7F2" stroke-width="2" stroke-linejoin="round"/>
              <path d="M14 12 V9 C14 6.8 15.8 5 18 5 C20.2 5 22 6.8 22 9 V12" 
                    fill="none" stroke="#FAF7F2" stroke-width="2" stroke-linecap="round"/>
              <circle cx="15.8" cy="20" r="2.8" fill="none" stroke="url(#hdr-accent)" stroke-width="1.8"/>
              <circle cx="20.2" cy="20" r="2.8" fill="none" stroke="#FAF7F2" stroke-width="1.8"/>
            </svg>
            <span class="brand-text">Co<span class="brand-accent">Shop</span></span>
          </div>
        </div>
        <div class="header-center">
          ${householdHtml}
        </div>
        <div class="header-right">
          ${userHtml}
        </div>
      </div>
    `;

    // Attach events
    this.el.querySelector('#brand-logo')?.addEventListener('click', () => this.callbacks.onLogoClick());
    this.el.querySelector('#household-btn')?.addEventListener('click', () => this.callbacks.onHouseholdClick());
    this.el.querySelector('#user-profile-btn')?.addEventListener('click', () => this.callbacks.onAuthClick());
    this.el.querySelector('#login-nav-btn')?.addEventListener('click', () => this.callbacks.onAuthClick());
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
